import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ClearButton } from '../../../../components/button/ClearButton';
import { validatePermission } from '../../../../containers/authorization-helpers/validate-permission';
import { PendingReason, PendingReasonMetadata } from '../../../../models/ordering/enums/pending-reason';
import { Permissions } from '../../../../models/enums/permissions';
import { OrderingSvc } from '../../../../modules/ordering/service';
import { WaitingReason } from '../../../../modules/ordering/models/waiting-on-someone';
import { IApplicationState } from '../../../../store/IApplicationState';
import { Routes } from '../../../../utils/app-constants';
import { useTileData } from './use-tile-data';

/**
 * Where each thing an order waits on is dealt with: the approvals page for it, and what a user needs to open that page
 */
export const WaitingReasonPages = [
    { reason: PendingReason.CreditHold, route: Routes.APPROVALS_CREDIT_HOLD, permission: Permissions.ApprovalsCreditHoldView },
    { reason: PendingReason.EngineeringReview, route: Routes.APPROVALS_ENGINEERING, permission: Permissions.ApprovalsEngineering },
    { reason: PendingReason.WindLoadReview, route: Routes.APPROVALS_WINDLOAD, permission: Permissions.ApprovalsWindLoad },
    { reason: PendingReason.CustomerServiceReview, route: Routes.APPROVALS_CUSTOMER_SERVICE, permission: Permissions.ApprovalsCustomerService },
    { reason: PendingReason.AddressReview, route: Routes.APPROVALS_ADDRESSES, permission: Permissions.ApprovalsAddressesView }
];

/**
 * The orders that are waiting on someone, and on what.  Everyone who has the dashboard sees how many, and the ones
 * who can open the page where a reason is dealt with get a link to it.
 */
export const WaitingOnSomeoneTile = () => {

    const permissions = useSelector((state: IApplicationState) => state.userdata.user.permissions);
    const [service] = React.useState(() => new OrderingSvc());
    const { data: waiting, failed, reload } = useTileData('waiting-on-someone', () => service.getWaitingOnSomeone());

    // the most of any reason is a whole bar, so the bars can be told apart
    const rows = WaitingReasonPages
        .map(page => ({ page, found: waiting?.reasons.find((x: WaitingReason) => x.pendingReason === page.reason) }))
        .filter(x => x.found !== undefined)
        .map(x => ({ page: x.page, count: x.found!.count }));
    const most = Math.max(1, ...rows.map(x => x.count));

    // what the orders have in common, which the counts are of
    const ahead = waiting && waiting.days > 0
        ? (waiting.days === 1 ? 'the next day' : `the next ${ waiting.days } days`)
        : undefined;

    const body = () => {
        if (failed) {
            return <div className="dashboard-tile-message">
                <p>Couldn't load what's waiting.</p>
                <ClearButton id="waiting-retry" text="Try again" icon="" classes="action" onBtnClick={ reload } />
            </div>;
        }

        if (!waiting) {
            return <p className="dashboard-tile-message">Loading…</p>;
        }

        if (waiting.total === 0) {
            return <p className="dashboard-tile-message">
                { ahead ? `Nothing with a cutoff in ${ ahead } is waiting on anyone.` : 'Nothing is waiting on anyone.' }
            </p>;
        }

        return <ul className="waiting-list">
            {
                rows.map(({ page, count }) => {
                    const name = PendingReasonMetadata[page.reason].display;
                    const canOpen = validatePermission({ currentPermissions: permissions, requiredPermissions: [page.permission] });

                    return <li key={ page.reason } className="waiting-row">
                        <div className="waiting-row-text">
                            {
                                canOpen
                                    ? <Link to={ page.route } className="waiting-row-name">{ name }</Link>
                                    : <span className="waiting-row-name">{ name }</span>
                            }
                            <span className="waiting-row-count">{ count.toLocaleString() }</span>
                        </div>
                        <div className="waiting-row-bar" aria-hidden="true">
                            <div className="waiting-row-bar-fill" style={ { width: `${ count / most * 100 }%` } } />
                        </div>
                    </li>;
                })
            }
        </ul>;
    };

    return <section className="dashboard-tile waiting-on-someone" aria-labelledby="waiting-on-someone-heading">
        <div className="dashboard-tile-heading">
            <h2 id="waiting-on-someone-heading">Pending orders</h2>
            {
                waiting && waiting.total > 0
                    ? <span className="dashboard-tile-total">{ waiting.total.toLocaleString() } { waiting.total === 1 ? 'order' : 'orders' }</span>
                    : null
            }
        </div>
        { ahead ? <p className="dashboard-tile-scope">Orders with a cutoff in { ahead }</p> : null }
        { body() }
    </section>;
};
